/**
 * Push notification utilities for Capacitor.
 *
 * Owns the native listener lifecycle, initial registration, token rotation,
 * and the "remember which token belongs to this device" bookkeeping. The
 * React layer (see `hooks/use-push-notifications.ts` and
 * `components/mobile/push-bootstrap.tsx`) delegates all native interaction
 * here so there's a single source of truth for listener handles.
 *
 * Notes:
 * - The `registration` listener is attached once at the module level and
 *   fires on every token issuance (initial + rotations). That fixes a bug
 *   where the previous one-shot Promise implementation dropped rotated
 *   tokens, leaving FCM to silently fail.
 * - All listener handles are kept so `teardownPushListeners()` can remove
 *   them cleanly on sign-out or during tests.
 * - Push is a progressive enhancement — every entry point swallows errors.
 */

import type { PluginListenerHandle } from '@capacitor/core'
import { isNativePlatform, getPlatform } from '@/lib/utils/platform'

export interface PushTokenInfo {
  token: string
  platform: 'ios' | 'android'
  deviceId: string | null
}

const DEVICE_ID_STORAGE_KEY = 'push_device_id'
const REGISTRATION_WATCHDOG_MS = 30_000

/**
 * Cached per-process device id. Populated lazily on first registration.
 */
let cachedDeviceId: string | null = null

/**
 * Returns a stable per-install device identifier, persisted in
 * `@capacitor/preferences`. Generated on first read (UUIDv4). On reinstall
 * the value resets — which is correct, because the FCM token also rotates
 * on reinstall, so the (user_id, device_id) UNIQUE constraint sees a fresh
 * pair and the previous row is left to age out via the stale-token sweeper.
 *
 * Returns null on web or if Preferences is unavailable. Callers must
 * tolerate null and fall back to the legacy "no device_id" code path.
 */
export async function getOrCreateDeviceId(): Promise<string | null> {
  if (!isNativePlatform()) return null
  if (cachedDeviceId) return cachedDeviceId

  try {
    const { Preferences } = await import('@capacitor/preferences')
    const { value } = await Preferences.get({ key: DEVICE_ID_STORAGE_KEY })
    if (value) {
      cachedDeviceId = value
      return value
    }
    const generated =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `dev_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`
    await Preferences.set({ key: DEVICE_ID_STORAGE_KEY, value: generated })
    cachedDeviceId = generated
    return generated
  } catch {
    return null
  }
}

type RegistrationListener = (info: PushTokenInfo) => void
type ReceivedListener = (title: string, body: string, data: Record<string, unknown>) => void
type TappedListener = (data: Record<string, unknown>) => void

/**
 * The token currently associated with this device session. Set on every
 * `registration` fire so sign-out can deactivate exactly this token without
 * guessing.
 */
let currentToken: string | null = null

/**
 * Module-level listener attachment flag. Prevents double-attachment when the
 * hook re-mounts under React StrictMode or when the user navigates between
 * routes that both call `setupPushListeners`.
 */
let nativeListenersAttached = false
let registrationHandles: PluginListenerHandle[] = []
let receivedHandles: PluginListenerHandle[] = []
let tappedHandles: PluginListenerHandle[] = []

/**
 * React-layer subscribers. Multiple components can subscribe without each
 * attaching their own native listener. The native listeners fan out to all
 * registered subscribers.
 */
const registrationSubscribers = new Set<RegistrationListener>()
const receivedSubscribers = new Set<ReceivedListener>()
const tappedSubscribers = new Set<TappedListener>()

/**
 * Return the token currently registered for this device, or null if no
 * successful registration has happened yet. Used by sign-out to deactivate
 * the correct row without issuing another native call.
 */
export function getCurrentPushToken(): string | null {
  return currentToken
}

/**
 * Clear the in-memory token reference (e.g. after successful deactivation).
 * Does NOT remove listeners.
 */
export function clearCurrentPushToken(): void {
  currentToken = null
}

/**
 * Subscribe to registration events. Fires on first token and every rotation.
 * Returns an unsubscribe function.
 */
export function onPushRegistration(fn: RegistrationListener): () => void {
  registrationSubscribers.add(fn)
  // If we already have a token from a previous registration, replay it to
  // newly-mounted subscribers so they can save/update immediately.
  if (currentToken) {
    try {
      fn({
        token: currentToken,
        platform: getPlatform() as 'ios' | 'android',
        deviceId: cachedDeviceId,
      })
    } catch {
      /* subscriber errors must not break the push path */
    }
  }
  return () => {
    registrationSubscribers.delete(fn)
  }
}

/**
 * Subscribe to foreground push deliveries. Returns an unsubscribe function.
 */
export function onPushReceived(fn: ReceivedListener): () => void {
  receivedSubscribers.add(fn)
  return () => {
    receivedSubscribers.delete(fn)
  }
}

/**
 * Subscribe to push taps (from any app state). Returns an unsubscribe fn.
 */
export function onPushTapped(fn: TappedListener): () => void {
  tappedSubscribers.add(fn)
  return () => {
    tappedSubscribers.delete(fn)
  }
}

/**
 * Attach the native push listeners once. Safe to call multiple times —
 * subsequent calls are no-ops. Must be called before `register()` so the
 * `registration` event isn't missed.
 */
export async function attachNativePushListeners(): Promise<void> {
  if (!isNativePlatform() || nativeListenersAttached) return
  nativeListenersAttached = true

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')

    // Persistent registration listener — fires on first token AND every
    // rotation. We fan out to every React subscriber so the token is saved
    // to the DB on every fire. We resolve `deviceId` first so subscribers
    // can persist (user_id, device_id) atomically with the token write.
    registrationHandles.push(
      await PushNotifications.addListener('registration', async (token) => {
        currentToken = token.value
        const deviceId = await getOrCreateDeviceId()
        const info: PushTokenInfo = {
          token: token.value,
          platform: getPlatform() as 'ios' | 'android',
          deviceId,
        }
        for (const fn of registrationSubscribers) {
          try {
            fn(info)
          } catch {
            /* swallow */
          }
        }
      })
    )

    registrationHandles.push(
      await PushNotifications.addListener('registrationError', (event) => {
        // Capture so we can alert on iOS push regressions. Lazy-import to
        // keep web bundles thin; the dynamic import is a no-op once
        // resolved so subsequent fires don't re-load the chunk.
        try {
          const platform = getPlatform()
          const errorMessage =
            (event as { error?: string } | undefined)?.error ?? 'unknown_registration_error'
          import('@sentry/nextjs')
            .then((Sentry) => {
              Sentry.captureMessage('Push notification registration failed', {
                level: 'warning',
                tags: { platform, push_phase: 'registration' },
                extra: { error: errorMessage },
              })
            })
            .catch(() => {})
        } catch {
          /* swallow — telemetry must never break the push path */
        }
      })
    )

    receivedHandles.push(
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        const title = notification.title ?? ''
        const body = notification.body ?? ''
        const data = (notification.data as Record<string, unknown>) ?? {}
        for (const fn of receivedSubscribers) {
          try {
            fn(title, body, data)
          } catch {
            /* swallow */
          }
        }
      })
    )

    tappedHandles.push(
      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const data = (action.notification.data as Record<string, unknown>) ?? {}
        for (const fn of tappedSubscribers) {
          try {
            fn(data)
          } catch {
            /* swallow */
          }
        }
      })
    )
  } catch {
    // If the plugin fails to load, mark as unattached so a later retry can
    // succeed (e.g. after a plugin refresh in development).
    nativeListenersAttached = false
  }
}

/**
 * Detach all native listeners. Used by tests and sign-out teardown.
 */
export async function teardownPushListeners(): Promise<void> {
  const allHandles = [...registrationHandles, ...receivedHandles, ...tappedHandles]
  for (const h of allHandles) {
    try {
      await h.remove()
    } catch {
      /* swallow */
    }
  }
  registrationHandles = []
  receivedHandles = []
  tappedHandles = []
  nativeListenersAttached = false
}

/**
 * Request permission (if not already granted) and call native register.
 * Returns true if registration was initiated; the actual token will arrive
 * via the persistent `registration` listener. Callers should subscribe via
 * `onPushRegistration` before invoking this, if they need the token.
 */
export async function requestAndRegister(): Promise<boolean> {
  if (!isNativePlatform()) return false

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')

    const permStatus = await PushNotifications.checkPermissions()
    if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
      const result = await PushNotifications.requestPermissions()
      if (result.receive !== 'granted') return false
    } else if (permStatus.receive !== 'granted') {
      return false
    }

    // Snapshot before register so the watchdog can detect a stalled mint.
    // On iOS this catches Firebase-Installations / APNs delays; on Android
    // it catches Google Play Services slow paths. ~1s is typical, 5–30s
    // happens on flaky networks, >30s usually means something is broken.
    const tokenBefore = currentToken
    await PushNotifications.register()

    setTimeout(() => {
      if (currentToken !== tokenBefore) return
      const platform = getPlatform()
      try {
        import('@sentry/nextjs')
          .then((Sentry) => {
            Sentry.captureMessage('Push registration stalled — no token after 30s', {
              level: 'warning',
              tags: { platform, push_phase: 'watchdog' },
            })
          })
          .catch(() => {})
        import('posthog-js')
          .then((m) => {
            m.default.capture('push_registration_pending_too_long', { platform })
          })
          .catch(() => {})
      } catch {
        /* swallow — telemetry must never break the push path */
      }
    }, REGISTRATION_WATCHDOG_MS)

    return true
  } catch {
    return false
  }
}

/**
 * Check the current native permission state without triggering a prompt.
 * Used by the primer to decide whether to show itself.
 */
export async function getPushPermissionState(): Promise<
  'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | 'unavailable'
> {
  if (!isNativePlatform()) return 'unavailable'
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    const status = await PushNotifications.checkPermissions()
    return status.receive
  } catch {
    return 'unavailable'
  }
}

/**
 * Clear every delivered notification from the system tray. Called on app
 * resume so the tray doesn't show stale items the user already read on web.
 */
export async function clearDeliveredNotifications(): Promise<void> {
  if (!isNativePlatform()) return
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    await PushNotifications.removeAllDeliveredNotifications()
  } catch {
    /* swallow */
  }
}

/**
 * Open the OS-level notification settings page for this app so a user who
 * previously denied permission can re-enable it. iOS uses the
 * `app-settings:` URL scheme; Android uses the WebView-supported intent URL
 * for `ACTION_APP_NOTIFICATION_SETTINGS`. Both work without an extra plugin.
 *
 * No-op on web. Returns true if a navigation was attempted.
 */
export async function openNativeNotificationSettings(): Promise<boolean> {
  if (!isNativePlatform()) return false

  try {
    const platform = getPlatform()

    if (platform === 'ios') {
      // iOS WKWebView honors the app-settings: URL scheme — opens the
      // Settings app directly to this app's preference page.
      window.location.href = 'app-settings:'
      return true
    }

    if (platform === 'android') {
      // Android WebView supports intent: URLs. This opens the system
      // Settings app on the per-app notification screen.
      window.location.href =
        'intent://#Intent;action=android.settings.APP_NOTIFICATION_SETTINGS;' +
        'S.android.provider.extra.APP_PACKAGE=net.alumnia.app;' +
        'package=com.android.settings;end'
      return true
    }
  } catch {
    /* swallow */
  }

  return false
}
